/**
 * Прогноз/MMS — сторінка комерційної диспетчеризації.
 *
 * Клон вигляду /forecast (та сама темна палітра), але власний контур даних:
 * усе для підрозділу «Прогноз» приходить одним запитом
 * GET /dispatch/forecast-mms/day?assetId&date — прогноз (з накладеними ручними
 * коригуваннями), факт, MMS, виконання прогнозу, хмарність і сонячна радіація.
 *
 * Прод-сторінка /forecast цим файлом не зачіпається: це окрема тека й окремий
 * маршрут, спільних компонентів немає.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useForecastMmsDate } from '@/hooks/useForecastMmsDate';
import ForecastMmsTiles, { type TileTotals } from './components/ForecastMmsTiles';
import ForecastMmsChart, { type HourRow } from './components/ForecastMmsChart';
import ForecastMmsTable from './components/ForecastMmsTable';
import MmsJournal from './components/MmsJournal';
import BessSchedule from './components/BessSchedule';
import { getStoredUser } from '@/hooks/useAuth';
import { useFcmmsTheme } from './theme';
// Ряд розділів малює каркас (DashboardLayout → OptSectionTabs) — однаково на
// всіх сторінках ряду. Сторінка лише питає, чи він є, щоб не дублювати
// заголовок над плиткою графіка.
import { useOptSectionsVisible } from '@/components/feature/OptSectionTabs';

interface DayResponse {
  assetId: string;
  date: string;
  today: string;
  closedThrough: number;
  hours: HourRow[];
  totals: {
    day: TileTotals;
    closed: TileTotals & { throughHour: number };
  };
  mms: { perAssetAvailable: boolean; seeded?: boolean; source?: string; revision?: number | null; status?: string | null; note?: string | null };
}

const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');

function SectionTitle({ icon, children, hint }: { icon: string; children: any; hint?: string }) {
  const T = useFcmmsTheme();
  return (
    <div className="flex items-baseline gap-3 flex-wrap mb-3">
      <div className="flex items-center gap-2">
        {/* Іконка секції — нейтральний тон (догма: червоний лише для помилки). */}
        <i className={icon} style={{ color: T.textMuted, fontSize: 18 }} />
        {/* Без uppercase: заголовок пишеться як звичайне слово з великої
            літери («Підсумки»). Другий заголовок цієї сторінки — абревіатура
            MMS, вона й так у капітелі, тож вигляд ряду не змінився. Трекінг
            зменшений: 0.06em розводило літери в мішаному регістрі. */}
        <h2 className="font-display text-base font-bold" style={{ color: T.textMain, letterSpacing: '0.01em' }}>{children}</h2>
      </div>
      {hint && <span className="text-xs" style={{ color: T.textDim }}>{hint}</span>}
    </div>
  );
}

export default function ForecastMmsPage() {
  const { t } = useTranslation();
  const T = useFcmmsTheme();
  // Чи стоїть над сторінкою ряд кнопок-розділів: якщо так, заголовок графіка
  // переїжджає всередину плитки, щоб не зливатися з ним в одну смугу.
  const showSections = useOptSectionsVisible();
  const { selectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id ?? '';
  // Лучки (стартовий статус, MMS-API ще нема): дозволяємо ручний бекфіл минулих
  // періодів у кабінеті. Гейт суворо по assetId — інші СЕС не зачіпаються.
  const LUCHKY_ID = 'ef2f4575-a1bd-408b-b867-e2d9618bfc18';
  const isLuchky = assetId === LUCHKY_ID;

  // Доба керується з топбара (винесено туди), а тут лише читається зі спільного
  // хука — тож ◀ дата ▶ доступні з будь-якого положення при скролі сторінки.
  const { date, setDate } = useForecastMmsDate();

  // Ті самі ролі, що й на бекенді (FORECAST_EDIT_ROLES). Кнопки лише ховаються —
  // справжня перевірка все одно робиться сервером на кожен запис.
  const canEdit = useMemo(() => {
    const role = getStoredUser()?.role;
    return role === 'Оператор' || role === 'CTO';
  }, []);

  // Версія графіка MMS — спільна для таблиці й журналу, щоб вони не розʼїхались.
  const [mmsRevision, setMmsRevision] = useState<number | null>(null);
  const [openRevisions, setOpenRevisions] = useState<number[]>([]);
  // Спільний сигнал: збереження в таблиці має одразу оновити журнал і список записів.
  const [refreshTick, setRefreshTick] = useState(0);
  // Єдина часова координата графіка й таблиці. Годину визначає графік — у нього
  // є вісь; таблиця лише підсвічує ту саму колонку, власного стану не тримає.
  const [activeHour, setActiveHour] = useState<number | null>(null);
  const afterSave = useCallback(() => { setRefreshTick((t) => t + 1); }, []);

  const [data, setData] = useState<DayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!assetId || !date) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    authFetch(`${API_BASE_URL}/dispatch/forecast-mms/day?assetId=${assetId}&date=${date}${mmsRevision ? `&revision=${mmsRevision}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: DayResponse) => setData(d))
      .catch((e) => { setData(null); setError(e?.message || i18n.t('ak_dsp_failed_to_load_data')); })
      .finally(() => setLoading(false));
  }, [assetId, date, mmsRevision]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout breadcrumb={[t('nav_forecast_mms')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: T.pageBg }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1400)}`} style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {!assetId && (
            <div className="rounded-xl p-6 text-center text-sm mb-6"
              style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, color: T.textMuted }}>
              {t('ak_dsp_select_a_station_in_the_top_bar_to_see')}
            </div>
          )}

          {error && (
            <div className="rounded-xl p-4 text-sm mb-6"
              style={{ background: 'var(--surface-2)', border: '1px solid color-mix(in srgb, var(--err) 35%, transparent)', color: T.bannerErrText }}>
              {error}
            </div>
          )}

          {/* ══ ПІДРОЗДІЛ: ПРОГНОЗ ═══════════════════════════════════════ */}
          {/* Заголовок «Прогноз», станцію та керування добою винесено в топбар
              (Topbar.tsx, гілка isFcmms) — там вони лишаються на виду при скролі.
              Тут одразу йдуть плитки підсумків. */}
          <div className="mb-5">
            <ForecastMmsChart hours={data?.hours ?? []} dateLabel={dmy(date)} loading={loading}
              onActiveHourChange={setActiveHour} titleInside={showSections}
              title={showSections ? t('ak_dsp_trading_schedule') : undefined} />
          </div>

          <div className="mb-8">
            <ForecastMmsTable
              hours={data?.hours ?? []}
              assetId={assetId}
              date={date}
              loading={loading}
              activeHour={activeHour}
              canEdit={canEdit}
              canEditMms={isLuchky || (!!mmsRevision && openRevisions.includes(mmsRevision))}
              forceUnlock={isLuchky}
              mmsRevision={mmsRevision}
              mmsStatus={data?.mms?.status ?? null}
              onSaved={() => { load(); afterSave(); }}
            />
          </div>

          {/* Розклад УЗЕ і журнал MMS показуємо ВСІМ, КРІМ кабінету ArtemK:
              там після «Погодинних даних» сторінка йде одразу до «Підсумків».
              Гейт — showSections: цей прапорець істинний рівно для ArtemK на
              маршрутах «Диспетчерської», тож окрема перевірка логіна тут була
              б дублем.

              УВАГА: разом із журналом у ArtemK зникають генерація, звіряння та
              ВІДПРАВКА графіка до MMS — це не косметика, а робоча операція.
              Стан (mmsRevision, openRevisions) лишився: сторінка й далі вміє
              підсвічувати обрану ревізію в таблиці, просто без самого журналу.
              Для решти акаунтів усе працює як раніше. */}
          {!showSections && (
            <>
              {/* Для активів з УЗЕ: графік заряду/розряду і підсумковий прогноз.
                  Компонент сам нічого не рендерить, якщо в активу немає УЗЕ. */}
              <BessSchedule assetId={assetId} date={date} canEdit={canEdit} onChanged={load} />

              {/* ══ ПІДРОЗДІЛ: MMS ═══════════════════════════════════════ */}
              <SectionTitle icon="ri-send-plane-line" hint={t('ak_dsp_delivery_schedule_and_statuses')}>MMS</SectionTitle>

              <MmsJournal
                assetId={assetId}
                date={date}
                canEdit={canEdit}
                selectedRevision={mmsRevision}
                onSelectRevision={setMmsRevision}
                onPickRecord={(d, rev) => { setDate(d); setMmsRevision(rev); }}
                onMeta={(m) => setOpenRevisions(m.openRevisions)}
                refreshTick={refreshTick}
                onChanged={() => { load(); afterSave(); }}
              />
            </>
          )}

          {/* Плитки підсумків опущені в самий низ (під «Збережені графіки») —
              лишаються доступні, але не займають верх сторінки. */}
          <div className="mt-8">
            <SectionTitle icon="ri-bar-chart-line" hint={t('ak_dsp_for_the_selected_day')}>{t('ak_dsp_summary')}</SectionTitle>
            <ForecastMmsTiles
              day={data?.totals.day ?? null}
              closed={data?.totals.closed ?? null}
              loading={loading}
              mmsNote={data?.mms?.note}
            />
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
}
